import type { SupportedLocale, TranslationKey } from "./i18n";

type CalendarEvent = {
  labelKey: TranslationKey;
  date: string;
  planned: boolean;
};

type SportSeason = {
  league: "MLB" | "NHL" | "NFL";
  leagueUrl: string;
  sportKey: TranslationKey;
  phaseKey: TranslationKey;
  phaseStartsOn: string;
  phaseEndsBefore: string;
  opening?: CalendarEvent;
};

export type PublicSportsCalendarRow = Pick<SportSeason, "league" | "leagueUrl" | "sportKey"> & {
  currentPhaseKey: TranslationKey | null;
  upcoming: CalendarEvent | null;
};

// Editorial sporting dates, independent of SportEx's authoritative market lifecycle.
// Update these windows for each season; an expired window never implies a new phase.
const sportSeasons: readonly SportSeason[] = [
  {
    league: "MLB",
    leagueUrl: "https://www.mlb.com/",
    sportKey: "guest.landing.sports.baseball",
    phaseKey: "guest.landing.sports.playoffs",
    phaseStartsOn: "2026-09-29",
    phaseEndsBefore: "2026-11-01",
  },
  {
    league: "NHL",
    leagueUrl: "https://www.nhl.com/",
    sportKey: "guest.landing.sports.hockey",
    phaseKey: "guest.landing.sports.regularSeason",
    phaseStartsOn: "2026-09-30",
    phaseEndsBefore: "2027-04-11",
    opening: { labelKey: "guest.landing.sports.playoffsStart", date: "2027-04-12", planned: true },
  },
  {
    league: "NFL",
    leagueUrl: "https://www.nfl.com/",
    sportKey: "guest.landing.sports.football",
    phaseKey: "guest.landing.sports.regularSeason",
    phaseStartsOn: "2026-09-09",
    phaseEndsBefore: "2027-01-11",
    opening: { labelKey: "guest.landing.sports.wildCardStart", date: "2027-01-16", planned: false },
  },
];

const torontoDate = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit",
});

export function getPublicSportsCalendar(now = new Date()): PublicSportsCalendarRow[] {
  const parts = torontoDate.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((value) => value.type === type)!.value;
  const today = `${part("year")}-${part("month")}-${part("day")}`;
  const rows: PublicSportsCalendarRow[] = sportSeasons.map((season) => ({
    league: season.league,
    leagueUrl: season.leagueUrl,
    sportKey: season.sportKey,
    currentPhaseKey: today >= season.phaseStartsOn && today < season.phaseEndsBefore ? season.phaseKey : null,
    upcoming: season.opening && today < season.opening.date ? season.opening : null,
  }));
  const priority = (row: PublicSportsCalendarRow) => row.upcoming ? 1 : row.currentPhaseKey ? 0 : 2;
  return rows.sort((a, b) => priority(a) - priority(b)
    || (a.upcoming?.date ?? "").localeCompare(b.upcoming?.date ?? ""));
}

export function formatPublicSportsDate(date: string, locale: SupportedLocale): string {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}
